import { createCrmContactCardTemplate } from '@/Containers/CrmSection/Components/Contacts'
import { copyableListView, renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'
import { formatPhone } from '@/Ship/Helpers/formatPhone'

import { CARRIER_CONTACT_FIELD_LABELS as L } from '../Contracts/CarrierContactFieldLabels'
import { CARRIER_CONTACT_COMMENT_REST_URL, CARRIER_CONTACT_REST_URL } from '../Contracts/ICarrier'

import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/** Лише перегляд: ставить сервер (аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

/**
 * Поля, яких бек від форми не приймає: службові ставить він сам, `carrier`
 * задається лише при створенні — змінити власника контакту не можна
 * (`SPEC-008` §5.1). Не шлемо їх, щоб тіло запиту не обіцяло зміни, якої не
 * буде.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'fullName',
    'externalCode',
    'isRemoved',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareContactPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    if (typeof formContext?.values?.['@id'] === 'string') delete values['carrier']
}

/** Картка контакту перевізника: стрічка праворуч — коментарі контакту, вкладка «Листи». */
const CarrierContactCardTemplate = createCrmContactCardTemplate({
    commentsRestUrl: CARRIER_CONTACT_COMMENT_REST_URL,
    lettersOwner: 'carrierContact',
})

/**
 * Контакти перевізника (`SPEC-010` §5.4, §7.3, §9.6, `SPEC-008` §4.2) — ті
 * самі поля, що в контакту клієнта; пошта підписана «E-mail», як у легасі.
 *
 * Власного реєстру немає — лише вкладка «Контакти» в картці перевізника, тож
 * без `pageUrl`. Створюють контакт тільки звідти: перевізник задається при
 * створенні й більше не змінюється (`CREATED_FROM_PARENT`).
 *
 * Права — лише з беку (`SPEC-003` §1.3): читати — хто бачить перевізника,
 * змінювати й видаляти — `_permissions` контакту (= `update` перевізника з
 * правилом «лише логіст», R-06). Ролей ресурс не питає.
 *
 * Видалений контакт перестає бути «Контактною особою» перевізника — це
 * робить бек у тій самій транзакції (R-08).
 */
export const CarrierContactResource: IResourceConfig = {
    title: 'Контакти перевізника',
    fields: {
        name: {
            label: L.name,
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        lastName: {
            label: L.lastName,
            inputProps: { maxLength: 150 },
        },
        position: {
            label: L.position,
            inputProps: { maxLength: 150 },
        },
        comment: {
            label: L.comment,
            type: 'textarea',
            inputProps: { maxLength: 4096 },
        },
        email: {
            label: L.email,
            inputProps: { maxLength: 150 },
            view: copyableListView(),
        },
        phone: {
            label: L.phone,
            type: 'phone',
            // У базі самими цифрами — у картці «+38 (067) 123-45-67» з копіюванням
            view: copyableListView(formatPhone),
        },
        fullNameEnglish: {
            label: L.fullNameEnglish,
            inputProps: { maxLength: 150 },
        },
        // Дата без часу (`SPEC-008` §4.2)
        birthDay: {
            label: L.birthDay,
            type: 'date',
            view: renderDateView,
        },
        createdAt: readOnly({
            label: L.createdAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        createdBy: readOnly({
            label: L.createdBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
        updatedAt: readOnly({
            label: L.updatedAt,
            type: 'datetime',
            view: renderDateTimeView,
        }),
        updatedBy: readOnly({
            label: L.updatedBy,
            type: 'select',
            resource: 'Setting.User',
            view: renderUsersView,
        }),
    },
    form: {
        eventHandlers: {
            onBeforeSendSaveRequest: {
                CarrierContactPayload: prepareContactPayload,
            },
        },
    },
    detailConfig: {
        modeConfig: {
            view: { template: CarrierContactCardTemplate },
            edit: { template: CarrierContactCardTemplate },
            create: { template: CarrierContactCardTemplate },
        },
    },
    // Як картка контакту клієнта: два стовпці блоків і стрічка коментарів праворуч
    drawer: {
        width: 1100,
    },
    restUrl: CARRIER_CONTACT_REST_URL,
    // Пошук у селекті «Контактна особа» (`SPEC-010` §5.4; легасі `_search`
    // без мертвого `patronymic`)
    settings: {
        searchFields: ['name', 'lastName', 'fullNameEnglish', 'email', 'phone'],
    },
}
